<template>
  <div class="gf_global-style__sidebar">
    <div class="d-flex align-items-center">
      <div class="gf_global-style__sidebar-button">
        <div class="gf_global-style__sidebar-button--back" @click.stop="back">
          <i class="icon icon-back" />
        </div>
      </div>
      <div v-if="hasOption" class="gf_global-style__sidebar-label button-14-medium txt-hybrid-shade-light-100">
        {{ presetName }}
      </div>
      <div v-else class="gf_global-style__sidebar-label-has-edited button-14-medium txt-hybrid-shade-light-100">
        <span v-popover:tooltip.top="presetName" class="gf_preset__preset-name">{{ presetName }}</span>
        <span class="gf_preset__edited" :class="{ 'gf_preset__edited--none': !isEditedCurrentPreset }">Edited</span>
      </div>
      <div v-if="hasOption" class="gf_global-style__sidebar-option">
        <GfDropdown
          :options="options"
          :value="nextPreset.type && nextPreset.type !== '' ? nextPreset.type : defaultOptionDrop"
          f-type="icon"
          dropdown-class="gf_global-style__sidebar-option_dropdown"
          b-class="paragraph-12-medium txt-hybrid-shade-light-100"
          dropdown-item-class="gf_global-style__sidebar-option_dropdown-item"
          @selected="changePreset">
        </GfDropdown>
      </div>
    </div>
    <div class="gf_global-style__sidebar-preview">
      <div v-if="linkedPresetId !== currentPreset.id">Preview on this page</div>
      <div v-else>You are previewing on current page</div>
      <PopupOnboarding
        v-if="isVisiblePopupPreviewOnboarding"
        type-o-b="previewPreset"
        :p-style="{
          position: 'absolute',
          right: '-302px',
          top: '20px',
        }"
        :style-arrow="{
          position: 'absolute',
          top: '25px',
          left: '-7px',
        }"
        title="Preview Preset"
        content="You can preview to see how it works on this page." />

      <PopupOnboarding
        v-if="isVisiblePopupPreviewModeOnboarding"
        type-o-b="previewMode"
        :p-style="{
          position: 'absolute',
          right: '-302px',
          top: '20px',
        }"
        :style-arrow="{
          position: 'absolute',
          top: '25px',
          left: '-7px',
        }"
        title="Quit Preset Preview Mode"
        content="You can continue editing the content freely when the Preset Preview Mode is switched off." />

      <GfToggle v-if="!isCurrentAppliedPreset" :checked="previewPreset" @on-change="onChangePreviewMode" />
    </div>
  </div>
</template>
<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent } from 'vue';
import GfDropdown from '@/core/components/base/GfDropdown.vue';
import { mapActions, mapMutations, mapState } from 'vuex';
import { mutations } from '@modules/editor-sidebar';
import PopupOnboarding from '../onboarding/PopupOnboarding.vue';
import { reRunInit } from '@modules/gem-meter/GemMeterEngine/Engine/useGemMeterController';

type DropdownOptionBasic = {
  name: string;
  value: string;
  icon: any;
  handleEdit: boolean;
  warning: any;
};

export default defineComponent({
  name: 'QuickNavigation',
  components: {
    GfDropdown,
    PopupOnboarding,
  },
  props: {
    defaultOptionDrop: {
      type: String,
      default: 'color',
    },
    options: {
      type: Array as PropType<Array<DropdownOptionBasic>>,
      default: () => [],
    },
    label: {
      type: String,
      default: '',
    },
    isEditedCurrentPreset: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['back', 'onChangeOption'],
  computed: {
    ...mapState('sidebar', {
      currentPreset: (state) => (state as any).currentPreset,
      nextPreset: (state) => (state as any).nextPreset,
      prevPreset: (state) => (state as any).prevPreset,
      previewPreset: (state) => (state as any).previewPreset,
      linkedPresetId: (state) => (state as any).linkedPresetId,
    }),
    ...mapState('app', {
      shopMeta: 'shopMeta',
    }),
    obGlobalStyle() {
      return this.shopMeta.onboardingGlobalStyle;
    },
    isVisiblePopupPreviewOnboarding() {
      return !this.obGlobalStyle.previewPreset && !this.isCurrentAppliedPreset;
    },
    isVisiblePopupPreviewModeOnboarding() {
      return !this.obGlobalStyle.previewMode && this.previewPreset && !this.isCurrentAppliedPreset;
    },
    isCurrentAppliedPreset() {
      return this.currentPreset.id === this.linkedPresetId;
    },
    hasOption() {
      return this.options && this.options.length > 0;
    },
    presetName() {
      if (this.label) return this.label;
      return this.currentPreset.name;
    },
  },
  methods: {
    ...mapMutations('sidebar', {
      stepPreset: mutations.SET_STEP_PRESET,
      setPreviewPreset: mutations.SET_PREVIEW_PRESET,
    }),
    ...mapActions('app', {
      updateShopMeta: 'updateShopMeta',
    }),
    back() {
      reRunInit();
      this.$emit('back', this.isEditedCurrentPreset);
    },
    changePreset(option: any) {
      const payload = {
        next: {
          type: '',
        },
        prev: this.prevPreset,
      };

      this.stepPreset(payload);
      this.$emit('onChangeOption', option);
    },
    onChangePreviewMode(val: boolean) {
      this.setPreviewPreset(val);
      reRunInit();
    },
  },
});
</script>
<style lang="scss" scoped>
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
.gf_global-style__sidebar {
  align-items: center;
  padding: 4px;
  background-color: $black2;
  box-shadow: 0px -4px 16px rgba(0, 0, 0, 0.06), 0px 4px 16px rgba(0, 0, 0, 0.12);

  &-button {
    margin-right: 4px;
    &--back {
      display: flex;
      align-items: center;
      width: 32px;
      height: 32px;
      padding: 8px;
      cursor: pointer;
      font-size: 16px;
      color: white;

      &:hover {
        // background: map-get($primary-blue-ui-kit, "325");
        background-color: $black3;
        border-radius: 3px;
      }
    }
  }

  &-label {
    flex: auto;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &-label-has-edited {
    display: flex;
    align-items: center;
    max-width: 100%;
    flex: 1;
    overflow: hidden;
    .gf_preset__preset-name {
      display: inline-block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: fit-content;
      line-height: 18px;
      max-width: 100%;
    }

    .gf_preset__edited {
      display: block;
      background: #fdb913;
      border-radius: 3px;
      font-size: 12px;
      padding: 0px 4px;
      color: #212121;
      font-weight: 500;
      margin-left: 4px;
      width: 45px;
      height: 16px;
      display: flex;
      align-items: center;
      &--none {
        display: none;
      }
    }
  }

  &-option {
    flex: 1;
    width: auto;
    margin-left: 4px;

    &:hover {
      // background: map-get($primary-blue-ui-kit, "325");
      background: $black3;
      border-radius: 3px;
    }

    :deep(.gf_global-style__sidebar-option_dropdown) {
      .gf_btn--dropdown {
        width: max-content;
        min-width: 98px;
        // background: map-get($primary-blue-ui-kit, "300");
        background: transparent;
        border: 0;
        font-size: 14px;

        &:hover {
          // background: map-get($primary-blue-ui-kit, "325");
          background: $black3;
          border-radius: 3px;
        }

        .gf_dropdown {
          &__label {
            font-family: 'Inter';
            font-weight: 500;
            color: map-get($hybrid-shade-light, '100');
          }

          &__svg {
            font-family: 'Inter';
            color: map-get($hybrid-shade-light, '100');
            width: 16px;
            height: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-left: 4px;

            path {
              fill: map-get($hybrid-shade-light, '100');
            }
          }
        }
      }

      .gf_dropdown--options {
        background-color: map-get($hybrid-shade-dark, '300');
        z-index: 99;
        box-shadow: 0px -4px 16px rgba(0, 0, 0, 0.06), 0px 4px 16px rgba(0, 0, 0, 0.12);
      }
    }
  }

  &-preview {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 16px 9px 36px;
    font-size: 12px;
    line-height: 14px;
    color: #e2e2e2;

    @media (max-width: 1300px) {
      font-size: 11px;
    }
  }
}
</style>
